import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import VerticalAlignCenterOutlinedIcon from '@mui/icons-material/VerticalAlignCenterOutlined';
import { brand } from '../../theme';
import { withAlpha } from '../../utils/annotationCategories';
import { downloadTextFile } from '../../utils/download';
import { safeFileName } from '../../utils/annotationExport';
import { formatTimecode } from '../../utils/timecode';
import {
  LineSpeaker,
  TimelineLine,
  TranscriptPlacement,
  activeLineIndex,
  languageName,
  linesOnTimeline,
  searchLines,
  toPlainText,
  toSrt,
  toWebVtt,
} from '../../utils/transcript';
import { fetchVideoTranscript } from '../../graphqlService';
import { MarkableLine, buildMarkableTranscript } from '../../utils/transcriptMarks';
import EmptyState from '../ui/EmptyState';
import { useVideoTranscript } from './useVideoTranscript';
import MarkableTranscriptView from './MarkableTranscript';
import type { TranscriptMarkingProps } from './transcriptMarking';

const TC_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, monospace';
/** Roughly how long the server takes per five minutes of audio. */
const SECONDS_PER_CHUNK = 15;

export interface TranscriptLineAction {
  start: number;
  end: number;
  text: string;
}

export interface TranscriptPanelProps {
  /** The video whose speech is transcribed: the sound angle of a multicam set. */
  videoId: string | null;
  /** Where that video sits on the timeline; omit for a single video. */
  placement?: TranscriptPlacement | null;
  /** Timeline seconds. */
  currentTime: number;
  playing: boolean;
  fps: number;
  onSeek: (seconds: number) => void;
  /** Start one automatically when there is none. */
  autoStart?: boolean;
  canStart?: boolean;
  /**
   * When the plan doesn't include transcripts: what to say instead of
   * "Transcribe", with an Upgrade button to Billing. A transcript that
   * already exists still shows.
   */
  upgrade?: { title: string; description: string } | null;
  /** Name for exported files. */
  exportName: string;
  /** A per-line "add a note" action (the creator's studio). */
  onAddNote?: (line: TranscriptLineAction) => void;
  /** Words to select and mark (Remove, Short, Note), and the notes shown in them; see MarkableTranscript. */
  marking?: TranscriptMarkingProps | null;
  listMaxHeight?: string | number;
  title?: string;
  testId?: string;
  /** Pause between polls of a transcript another page is working on (tests shorten it). */
  pollMs?: number;
  /** Who says a line (timeline seconds), for a multicam set that knows who speaks when; null when unsure. */
  speakerAt?: LineSpeaker;
}

/** The query's words marked in the text. */
const highlight = (text: string, query: string): React.ReactNode => {
  const terms = query.trim().split(/\s+/).filter(Boolean).map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (terms.length === 0) return text;
  const parts = text.split(new RegExp(`(${terms.join('|')})`, 'gi'));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Box component="mark" key={i} sx={{ bgcolor: withAlpha(brand.copper, 0.22), color: 'inherit', borderRadius: 0.5, px: 0.25 }}>
        {part}
      </Box>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const minutesLeft = (chunksLeft: number) => {
  const minutes = Math.ceil((chunksLeft * SECONDS_PER_CHUNK) / 60);
  return minutes <= 1 ? 'about a minute left' : `about ${minutes} minutes left`;
};

interface LineRowProps {
  line: TimelineLine;
  fps: number;
  active: boolean;
  query: string;
  onSeek: (seconds: number) => void;
  onAddNote?: (line: TranscriptLineAction) => void;
}

const LineRow = React.memo<LineRowProps>(({ line, fps, active, query, onSeek, onAddNote }) => (
  <Box
    data-line-index={line.index}
    data-active={active ? 'true' : 'false'}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 0.5,
      borderRadius: 2,
      borderLeft: `3px solid ${active ? brand.copper : 'transparent'}`,
      bgcolor: active ? withAlpha(brand.copper, 0.08) : 'transparent',
      '&:hover': { bgcolor: active ? withAlpha(brand.copper, 0.12) : brand.ivory },
      '&:hover .transcript-line-action, &:focus-within .transcript-line-action': { opacity: 1 },
    }}
  >
    <ButtonBase
      onClick={() => onSeek(line.start)}
      aria-current={active ? 'true' : undefined}
      aria-label={`${formatTimecode(line.start, fps)}: ${line.speaker ? `${line.speaker}: ` : ''}${line.text}`}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 1.25,
        textAlign: 'left',
        px: 1.25,
        py: 0.75,
        borderRadius: 2,
        '&.Mui-focusVisible': { outline: `2px solid ${brand.copper}`, outlineOffset: -2 },
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{ fontFamily: TC_FONT, fontVariantNumeric: 'tabular-nums', color: active ? 'primary.dark' : 'text.secondary', pt: '2px', flexShrink: 0 }}
      >
        {formatTimecode(line.start, fps)}
      </Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55, overflowWrap: 'anywhere' }}>
        {line.speaker && <Box component="span" data-testid="transcript-speaker" sx={{ fontWeight: 700, mr: 0.75 }}>{line.speaker}</Box>}
        {highlight(line.text, query)}
      </Typography>
    </ButtonBase>
    {onAddNote && (
      <Tooltip title="Add a note on this line">
        <IconButton
          size="small"
          className="transcript-line-action"
          aria-label={`Add a note at ${formatTimecode(line.start, fps)}`}
          onClick={() => onAddNote({ start: line.start, end: line.end, text: line.text })}
          sx={{ mt: 0.5, mr: 0.5, opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms', color: 'text.secondary', '&:focus-visible': { opacity: 1 } }}
        >
          <NoteAddOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
));
LineRow.displayName = 'LineRow';

type ExportFormat = 'srt' | 'vtt' | 'txt';

/**
 * The transcript of a video's speech beside the player: read along, search,
 * click a line to go there, export captions. While it is being made, lines
 * appear as each five minutes is done.
 */
const TranscriptPanel: React.FC<TranscriptPanelProps> = ({
  videoId,
  placement,
  currentTime,
  playing,
  fps,
  onSeek,
  autoStart = false,
  canStart = true,
  upgrade = null,
  exportName,
  onAddNote,
  marking = null,
  listMaxHeight = 'calc(100vh - 300px)',
  title = 'Transcript',
  testId = 'transcript-panel',
  pollMs,
  speakerAt,
}) => {
  // Marking needs every word's time.
  const markingOn = Boolean(marking);
  const state = useVideoTranscript(videoId, { autoStart, canStart, pollMs, withWords: markingOn });
  const { phase, transcript, problem, starting } = state;
  const markable = useMemo(
    () => (markingOn ? buildMarkableTranscript(state.lines, placement) : null),
    [markingOn, state.lines, placement]
  );
  const lines = useMemo(() => {
    if (!markable) return linesOnTimeline(state.lines, placement, speakerAt);
    if (!speakerAt) return markable.lines;
    // Marking draws its own rows; they name who's speaking the same way.
    return markable.lines.map((line) => {
      const speaker = speakerAt(line.start, Math.max(line.start, line.end));
      return speaker ? { ...line, speaker } : line;
    });
  }, [markable, state.lines, placement, speakerAt]);
  const [query, setQuery] = useState('');
  const [follow, setFollow] = useState(true);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProblem, setExportProblem] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const matches = useMemo(() => (query.trim() ? searchLines(lines, query) : null), [lines, query]);
  const visible = matches ? matches.map((position) => lines[position]) : lines;
  const activeIndex = activeLineIndex(lines, currentTime);
  const activeLine = activeIndex >= 0 ? lines[activeIndex] : null;

  // Keep the spoken line in view while playing (scrolling the list, not the page).
  useEffect(() => {
    if (!follow || !playing || !activeLine || query) return;
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>(`[data-line-index="${activeLine.index}"]`);
    if (!list || !row) return;
    // Measured on screen, so it holds whatever the rows are positioned against.
    const top = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    const target = top - list.clientHeight / 3;
    // Marking keeps a bar at the bottom of the list: keep the line clear of it.
    const bottomMargin = markingOn ? 150 : 48;
    if (top < list.scrollTop + 24 || top > list.scrollTop + list.clientHeight - bottomMargin) {
      if (typeof list.scrollTo === 'function') list.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      else list.scrollTop = Math.max(0, target);
    }
  }, [activeLine, follow, playing, query, markingOn]);

  const runExport = async (format: ExportFormat) => {
    setExportAnchor(null);
    if (!videoId) return;
    setExporting(true);
    setExportProblem(null);
    try {
      // Captions split long lines at word boundaries, so they need the words' times.
      const full = format === 'txt' ? null : await fetchVideoTranscript(videoId, { withLines: true, withWords: true });
      const exportLines = full ? linesOnTimeline(full.segments ?? [], placement, speakerAt) : lines;
      const base = safeFileName(`${exportName}-transcript`, 'transcript');
      if (format === 'srt') downloadTextFile(`${base}.srt`, toSrt(exportLines), 'application/x-subrip;charset=utf-8');
      else if (format === 'vtt') downloadTextFile(`${base}.vtt`, toWebVtt(exportLines), 'text/vtt;charset=utf-8');
      else downloadTextFile(`${base}.txt`, toPlainText(exportLines, exportName));
    } catch (error) {
      console.error('Transcript export failed:', error);
      setExportProblem('The transcript couldn’t be exported. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const working = phase === 'working' || starting;
  const progress = transcript ? Math.round((transcript.progress || 0) * 100) : 0;
  const chunksLeft = transcript ? Math.max(0, transcript.chunkCount - transcript.chunksDone) : 0;
  const statusChip = phase === 'done'
    ? languageName(transcript?.language) ?? 'Ready'
    : working
      ? `Transcribing ${progress}%`
      : phase === 'failed'
        ? 'Stopped'
        : null;

  const renderBody = () => {
    if (phase === 'loading') {
      return (
        <Box sx={{ p: 1.5, display: 'grid', gap: 1.25 }} aria-label="Loading the transcript" role="status">
          {[0, 1, 2, 3].map((row) => <Skeleton key={row} variant="rounded" height={36} />)}
        </Box>
      );
    }
    if (phase === 'error') {
      return (
        <Alert severity="error" sx={{ m: 1.5 }} action={<Button color="inherit" size="small" onClick={state.retry}>Retry</Button>}>
          {problem}
        </Alert>
      );
    }
    if (phase === 'none' && !starting && upgrade) {
      return (
        <Box sx={{ p: 1 }} data-testid="transcript-upgrade">
          <EmptyState
            compact
            icon={<RecordVoiceOverOutlinedIcon />}
            title={upgrade.title}
            description={upgrade.description}
            actionLabel="Upgrade"
            onAction={() => window.location.assign('/billing')}
          />
        </Box>
      );
    }
    if (phase === 'none' && !starting) {
      return (
        <Box sx={{ p: 1 }}>
          <EmptyState
            compact
            icon={<RecordVoiceOverOutlinedIcon />}
            title={canStart ? 'Transcribe the conversation' : 'No transcript yet'}
            description={canStart
              ? 'Every line, time-coded: read along, search for a moment, click to jump there, and export captions. It takes about a minute for every 20 minutes of video. Speech recognition that Cloudflare runs for us makes it, so check names and numbers.'
              : 'There is no transcript yet.'}
            actionLabel={canStart ? 'Transcribe' : undefined}
            onAction={canStart ? () => void state.start() : undefined}
          />
          {problem && <Alert severity="warning" sx={{ mx: 1, mb: 1 }}>{problem}</Alert>}
        </Box>
      );
    }
    return (
      <>
        {working && (
          <Box sx={{ px: 2, pt: 1.5 }} role="status" aria-live="polite">
            <LinearProgress variant={transcript ? 'determinate' : 'indeterminate'} value={progress} aria-label="Transcript progress" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {transcript
                ? `Transcribing… ${progress}%, ${minutesLeft(chunksLeft)}. Lines appear as they're ready, and it carries on if you leave this page.`
                : 'Starting the transcript…'}
            </Typography>
          </Box>
        )}
        {phase === 'failed' && (
          <Alert
            severity="warning"
            sx={{ m: 1.5, mb: 0 }}
            action={canStart ? <Button color="inherit" size="small" onClick={() => void state.start()} disabled={starting}>Try again</Button> : undefined}
          >
            {transcript?.error || 'The transcript stopped before it finished.'}
          </Alert>
        )}
        {lines.length === 0 ? (
          !working && phase === 'done' ? (
            <Box sx={{ p: 1 }}>
              <EmptyState compact icon={<SubjectOutlinedIcon />} title="No speech found" description="The transcript didn’t find any words in this recording." />
            </Box>
          ) : null
        ) : visible.length === 0 ? (
          <Box sx={{ p: 1 }}>
            <EmptyState
              compact
              icon={<SearchIcon />}
              title="No lines match"
              description="Try other words."
              secondaryActionLabel="Clear search"
              onSecondaryAction={() => setQuery('')}
            />
          </Box>
        ) : markable && marking ? (
          <MarkableTranscriptView
            transcript={markable}
            lines={visible as MarkableLine[]}
            query={query}
            fps={fps}
            activeLine={activeLine ? activeLine.index : null}
            marking={marking}
            onSeek={onSeek}
            onAddNote={onAddNote}
            scrollRef={listRef}
          />
        ) : (
          <Box sx={{ p: 1, display: 'grid', gap: 0.25 }}>
            {visible.map((line) => (
              <LineRow
                key={line.index}
                line={line}
                fps={fps}
                active={activeLine?.index === line.index}
                query={query}
                onSeek={onSeek}
                onAddNote={onAddNote}
              />
            ))}
          </Box>
        )}
      </>
    );
  };

  const hasLines = lines.length > 0;
  return (
    <Paper
      variant="outlined"
      data-testid={testId}
      sx={{ borderRadius: 3, borderColor: brand.border, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${brand.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hasLines ? 1.25 : 0, minHeight: 32 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, fontSize: 16 }}>
            {title}
          </Typography>
          {statusChip && (
            <Chip size="small" label={statusChip} sx={{ height: 22, fontVariantNumeric: 'tabular-nums' }} data-testid="transcript-status" />
          )}
          <Box sx={{ flex: 1 }} />
          {hasLines && (
            <Tooltip title={follow ? 'Following playback' : 'Follow playback'}>
              <IconButton
                size="small"
                aria-label="Follow playback"
                aria-pressed={follow}
                onClick={() => setFollow((value) => !value)}
                sx={{ color: follow ? 'primary.main' : 'text.secondary' }}
              >
                <VerticalAlignCenterOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasLines && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              endIcon={<ExpandMoreIcon />}
              onClick={(event) => setExportAnchor(event.currentTarget)}
              disabled={exporting}
              aria-haspopup="menu"
              aria-expanded={Boolean(exportAnchor)}
            >
              Export
            </Button>
          )}
        </Box>
        {hasLines && (
          <TextField
            size="small"
            fullWidth
            placeholder="Search the transcript"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Search the transcript' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {matches?.length ?? 0} of {lines.length}
                  </Typography>
                  <IconButton size="small" aria-label="Clear search" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        )}
        {exportProblem && <Alert severity="error" sx={{ mt: 1.25 }}>{exportProblem}</Alert>}
      </Box>

      <Box ref={listRef} sx={{ position: 'relative', overflowY: 'auto', maxHeight: listMaxHeight, minHeight: 160 }}>
        {renderBody()}
      </Box>

      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 280 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Times match {placement ? 'the multicam timeline' : 'the video'}.
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => void runExport('srt')}>
          <ListItemText primary="Captions (.srt)" secondary="Premiere Pro, DaVinci Resolve, Final Cut Pro, YouTube" />
        </MenuItem>
        <MenuItem onClick={() => void runExport('vtt')}>
          <ListItemText primary="Web captions (.vtt)" secondary="Browsers and web players" />
        </MenuItem>
        <MenuItem onClick={() => void runExport('txt')}>
          <ListItemText primary="Text with timecodes (.txt)" secondary="For reading, notes and scripts" />
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default TranscriptPanel;
